import type { ModuleManifest, NavEntry } from './manifest';
import type { PermissionChecker } from '../permissions/engine';
import { aggregateCatalogs } from '../permissions/define-permissions';
import type { PermissionString } from '../permissions/types';

/**
 * Composes module manifests into the shell (backbone §2/§3).
 * Registering a module is one line here; nothing else in the repo changes.
 */
export class ModuleRegistry {
  readonly modules: readonly ModuleManifest[];

  constructor(modules: readonly ModuleManifest[]) {
    assertUniqueBasePaths(modules);
    this.modules = modules;
  }

  /** The aggregated app catalog the permission engine expands wildcards against. */
  get permissionCatalog(): readonly PermissionString[] {
    return aggregateCatalogs(this.modules.map((m) => m.permissionCatalog));
  }

  get i18nNamespaces(): readonly string[] {
    return [...new Set(this.modules.flatMap((m) => m.i18nNamespaces))];
  }

  /**
   * Nav generated from the same declarations that guard the routes, filtered recursively —
   * a user never sees a menu entry leading to a 403.
   */
  visibleNav(
    checker: PermissionChecker,
    isFlagEnabled: (flag: string) => boolean = () => true,
  ): NavEntry[] {
    const filter = (entries: readonly NavEntry[]): NavEntry[] =>
      entries.flatMap((entry) => {
        if (entry.flag !== undefined && !isFlagEnabled(entry.flag)) return [];
        if (entry.permissions !== undefined && !checker.canGroups(entry.permissions)) return [];
        if (entry.children === undefined) return [entry];
        const children = filter(entry.children);
        // A parent whose children are all filtered away would be a dead-end menu node.
        if (children.length === 0 && entry.children.length > 0) return [];
        return [{ ...entry, children }];
      });
    return filter(this.modules.flatMap((m) => m.nav));
  }

  /**
   * The registry of one host (decision T-59): the listed modules, in registration order. An id
   * no module registers is a configuration error and refuses loudly — a typo in `config.json`
   * must not silently serve an empty console.
   */
  select(ids: readonly string[]): ModuleRegistry {
    const known = new Set(this.modules.map((m) => m.id));
    const unknown = ids.filter((id) => !known.has(id));
    if (unknown.length > 0) {
      throw new Error(`config.json modules names unknown module(s): ${unknown.join(', ')}`);
    }
    return new ModuleRegistry(this.modules.filter((m) => ids.includes(m.id)));
  }

  /**
   * Where `/` leads on a host with a selected module set (SDD-024 A-5): the first nav entry the
   * user may open, else the first entry at all (its guard then answers 403), else `undefined`.
   */
  homePath(checker: PermissionChecker): string | undefined {
    const visible = this.visibleNav(checker);
    const first = visible[0] ?? this.modules.flatMap((m) => m.nav)[0];
    return first?.to;
  }

  /** "No products assigned" replaces the legacy coarse `ui.*` entry gate (Addendum A.2). */
  hasAnyAccess(checker: PermissionChecker): boolean {
    return this.visibleNav(checker).length > 0;
  }
}

export function assertUniqueBasePaths(modules: readonly ModuleManifest[]): void {
  const seen = new Map<string, string>();
  for (const module of modules) {
    const owner = seen.get(module.basePath);
    if (owner !== undefined) {
      throw new Error(
        `Route namespace collision: "${module.basePath}" is claimed by both "${owner}" and "${module.id}".`,
      );
    }
    seen.set(module.basePath, module.id);
  }
}
